import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {} from '@angular/common';
import { MatFormFieldModule, MatHint, MatLabel } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { takeUntil } from 'rxjs';
import { FormlyErrorComponent } from '../formly-error.component';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { FieldLabelRowComponent } from './field-label-row.component';
import { commitUserValue } from './user-edit.util';

interface Star {
  value: number;
}

@Component({
  selector: 'formly-field-rating',
  template: `
    <div class="flex flex-col w-full mb-4" [formlyDataCy]="field">
      @if (props.label) {
        <mat-label class="inline-block mb-2 font-medium">
          <app-field-label-row
            [label]="props.label + (props.required ? ' *' : '')"
            [required]="props.required"
            [info]="props.info"></app-field-label-row>
        </mat-label>
      }

      <div
        class="flex items-center"
        [class.opacity-70]="props.disabled"
        [class.pointer-events-none]="props.disabled"
        (mouseleave)="hoverValue = 0">
        <div class="flex items-center">
          @for (star of stars; track star.value) {
            <div
              class="relative cursor-pointer inline-flex items-center justify-center"
              (mousemove)="!props.disabled && handleStarHover(star.value, $event)"
              (click)="!props.disabled && handleStarClick(star.value, $event)">
              <mat-icon
                class="text-3xl w-8 h-8 transition-colors duration-200"
                [class.text-amber-400]="isStarActive(star.value)"
                [class.text-gray-300]="!isStarActive(star.value)">
                {{ isStarFilled(star.value) ? 'star' : 'star_outline' }}
              </mat-icon>
              @if (isHalfStar(star.value)) {
                <mat-icon class="text-3xl w-8 h-8 absolute left-0 text-amber-400"> star_half </mat-icon>
              }
            </div>
          }
        </div>
        @if (showRatingValue) {
          <div class="ml-2 font-medium min-w-7 text-center">
            {{ currentRatingValue }}
          </div>
        }
      </div>

      @if (field.props.description) {
        <mat-hint class="inline-block mt-1 mb-2">
          {{ field.props.description }}
        </mat-hint>
      }
      <app-formly-error [field]="field"></app-formly-error>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    FormlyModule,
    MatFormFieldModule,
    MatIconModule,
    MatTooltipModule,
    MatLabel,
    MatHint,
    FormlyErrorComponent,
    FormlyDataCyDirective,
    FieldLabelRowComponent,
  ],
})
export class FormlyFieldRatingComponent extends FieldType implements OnInit {
  destroy$ = injectDestroy();

  stars: Star[] = [];
  hoverValue = 0;
  showRatingValue = true;

  get currentRatingValue(): string {
    return this.hoverValue > 0 ? this.hoverValue.toString() : this.formControl.value || '0';
  }

  get allowHalfStar(): boolean {
    return this.field.props?.allowHalfStar !== false;
  }

  ngOnInit(): void {
    this.initializeComponent();

    this.formControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {});
  }

  private initializeComponent(): void {
    this.initializeStars();
    this.applyDefaultValue();
    this.showRatingValue = this.field.props?.showRatingValue !== false;
  }

  private initializeStars(): void {
    const maxRating = this.field.props?.maxRating || 5;
    this.stars = Array.from({ length: maxRating }, (_, i) => ({ value: i + 1 }));
  }

  private applyDefaultValue(): void {
    if (this.field.props?.defaultValue !== undefined && !this.formControl.value) {
      this.formControl.setValue(this.field.props.defaultValue);
    }
  }

  isStarActive(value: number): boolean {
    const currentValue = this.hoverValue || this.formControl.value || 0;
    return value <= Math.ceil(currentValue);
  }

  isStarFilled(value: number): boolean {
    const currentValue = this.hoverValue || this.formControl.value || 0;
    return value <= currentValue;
  }

  isHalfStar(value: number): boolean {
    const currentValue = this.hoverValue || this.formControl.value || 0;
    return value - 0.5 === currentValue;
  }

  handleStarHover(value: number, event: MouseEvent): void {
    if (!this.allowHalfStar) {
      this.hoverValue = value;
      return;
    }

    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const isLeftHalf = event.clientX - rect.left < rect.width / 2;

    this.hoverValue = isLeftHalf ? value - 0.5 : value;
  }

  handleStarClick(value: number, event: MouseEvent): void {
    let newValue = value;

    if (this.allowHalfStar) {
      const target = event.currentTarget as HTMLElement;
      const rect = target.getBoundingClientRect();
      const isLeftHalf = event.clientX - rect.left < rect.width / 2;
      newValue = isLeftHalf ? value - 0.5 : value;
    }

    // Toggle rating off if clicking the same value
    if (this.formControl.value === newValue) {
      newValue = 0;
    }

    commitUserValue(this.formControl, newValue);
    this.hoverValue = 0;
  }
}
